import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SaasError, saasVerifyEmail } from '../lib/saas-client';

type Status = 'verifying' | 'ok' | 'error' | 'missing';

export function VerifyEmailPanel() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'missing');
  const [error, setError] = useState<string | null>(null);
  // The token is one-time-use; this guard stops React StrictMode's double
  // effect from consuming it twice and turning a success into a failure.
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    saasVerifyEmail(token)
      .then(() => setStatus('ok'))
      .catch((err) => {
        setError(err instanceof SaasError ? err.message : 'Verification failed.');
        setStatus('error');
      });
  }, [token]);

  return (
    <div className="auth-page">
      <section className="panel auth-panel">
        {status === 'missing' && (
          <header className="page-header">
            <h1>Verification link missing</h1>
            <p>
              This page expects a one-time token in the URL. Use the link from
              your verification email.
            </p>
          </header>
        )}
        {status === 'verifying' && (
          <header className="page-header">
            <h1>Verifying…</h1>
            <p>Confirming your email address.</p>
          </header>
        )}
        {status === 'ok' && (
          <>
            <header className="page-header">
              <h1>Email verified</h1>
              <p>Your account is active. You can sign in now.</p>
            </header>
            <p className="auth-switch">
              <Link to="/login">Sign in →</Link>
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <header className="page-header">
              <h1>Verification failed</h1>
              <p>{error ?? 'This link is invalid or has expired.'}</p>
            </header>
            <p className="auth-switch">
              <Link to="/login">← Back to sign in</Link>
            </p>
          </>
        )}
      </section>
    </div>
  );
}
